import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputAdornment, TextField, Typography } from '@mui/material';
import { numToCurrency, downloadExcel, downloadPdf, getSalesStatusCodes, netHST } from '../../../utils/Functions';
import { selectProject } from '../../../features/project/projectSlice';
import StandardTable from '../../tables/StandardTable';
import { IUnit } from '../../../types/unit';
import { customTierUnits } from '../../../utils/CustomSettings';
import { Flex } from '../../../commonStyles';

const DEFAULT_RANGE_SIZE = 100000;
const MAX_BUCKETS = 200;

const RangeSummary = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const { units, tierType, statusType } = props;

  const [rangeSizeInput, setRangeSizeInput] = useState<string>(String(DEFAULT_RANGE_SIZE));
  const [startValueInput, setStartValueInput] = useState<string>('');
  const [endValueInput, setEndValueInput] = useState<string>('');

  // The table below recomputes (and re-renders every bucket row) on every change, so it's debounced
  // - typing/deleting digits on the way to a final value (eg. "100000" -> "10000" -> ... -> "1")
  // shouldn't each trigger a recompute/re-render against whatever tiny intermediate value is
  // on-screen at that moment. Blurring the field or pressing Enter commits immediately instead of
  // waiting out the debounce, since that's a clear signal the user is done editing.
  const [debouncedRangeSizeInput, setDebouncedRangeSizeInput] = useState(rangeSizeInput);
  const [debouncedStartValueInput, setDebouncedStartValueInput] = useState(startValueInput);
  const [debouncedEndValueInput, setDebouncedEndValueInput] = useState(endValueInput);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedRangeSizeInput(rangeSizeInput);
      setDebouncedStartValueInput(startValueInput);
      setDebouncedEndValueInput(endValueInput);
    }, 600);
    return () => clearTimeout(timeout);
  }, [rangeSizeInput, startValueInput, endValueInput]);

  const commitNow = () => {
    setDebouncedRangeSizeInput(rangeSizeInput);
    setDebouncedStartValueInput(startValueInput);
    setDebouncedEndValueInput(endValueInput);
  };

  const commitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitNow();
  };

  const rangeSize = Number(debouncedRangeSizeInput) > 0 ? Number(debouncedRangeSizeInput) : DEFAULT_RANGE_SIZE;

  const rangeColumns = useMemo(() => {
    return [
      {
        Header: 'Range',
        accessor: (rowData: any) => {
          return `${numToCurrency.format(rowData.minRange)} to ${numToCurrency.format(rowData.maxRange)}`;
        },
      },
      {
        Header: 'Total Units',
        accessor: (rowData: any) => `${rowData.units} (${((rowData.units / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Accumulative Unit Count',
        accessor: (rowData: any) => `${rowData.accumulativeUnit} (${((rowData.accumulativeUnit / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Sold',
        accessor: (rowData: any) => `${rowData.sold} (${((rowData.sold / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Accumulative Sold',
        accessor: (rowData: any) => `${rowData.accumulativeSold} (${((rowData.accumulativeSold / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Available',
        accessor: (rowData: any) => `${rowData.available} (${((rowData.available / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Accumulative Available',
        accessor: (rowData: any) =>
          `${rowData.accumulativeAvailable} (${((rowData.accumulativeAvailable / rowData.total) * 100).toFixed(2)}%)`,
      },
      {
        Header: 'Revenue',
        accessor: (rowData: any) => rowData.revenue,
      },
      {
        Header: 'Accumulative Revenue',
        accessor: (rowData: any) => rowData.accumulativeRevenue,
      },
    ];
  }, []);

  const rangeData = useMemo(() => {
    if (!units.length) return [];
    let allUnits = units;

    if (tierType) {
      allUnits = units.filter((unit: IUnit) => customTierUnits(project._id, units, tierType).includes(unit.tier));
    }

    const isNetPriceProject = statusType === 'After HST Rebate' && !!project.netHst;
    const priceOf = (unit: IUnit) => (isNetPriceProject ? netHST(unit.basePrice, project) : unit.basePrice);

    let autoMin =
      Math.floor(
        allUnits.reduce((min: any, unit: any) => (priceOf(unit) < min ? priceOf(unit) : min), priceOf(allUnits[0])) / rangeSize
      ) * rangeSize;
    let autoMax = allUnits.reduce((max: any, unit: any) => (priceOf(unit) > max ? priceOf(unit) : max), priceOf(allUnits[0]));

    let min =
      debouncedStartValueInput !== '' && !isNaN(Number(debouncedStartValueInput))
        ? Math.floor(Number(debouncedStartValueInput) / rangeSize) * rangeSize
        : autoMin;
    let max =
      debouncedEndValueInput !== '' && !isNaN(Number(debouncedEndValueInput)) ? Number(debouncedEndValueInput) : autoMax;

    let rangeArray: IRange[] = [];
    let totalRevenue = allUnits.reduce((a: any, b: any) => a + priceOf(b), 0);
    let salesStatusCodes = getSalesStatusCodes(project);

    // Running accumulators carried forward bucket-to-bucket, rather than re-summing every prior
    // bucket on each iteration - keeps this O(buckets) instead of O(buckets^2), which matters once
    // a small range difference produces hundreds of buckets.
    let accumulative = 0;
    let accumulativeRevenue = 0;
    let accumulativeSold = 0;
    let accumulativeAvailable = 0;

    let bucketCount = Math.min(Math.ceil((max - min) / rangeSize), MAX_BUCKETS);

    for (let i = min / rangeSize; i < min / rangeSize + bucketCount; i++) {
      let bucketMin = i * rangeSize;
      let bucketMax = i * rangeSize + (rangeSize - 1);
      let unitsInBucket = allUnits.filter((unit: IUnit) => bucketMin <= priceOf(unit) && priceOf(unit) <= bucketMax);

      let total = unitsInBucket.length;
      let sold = unitsInBucket.filter((unit: IUnit) => salesStatusCodes.includes(unit.status)).length;
      let available = unitsInBucket.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status)).length;
      let revenue = unitsInBucket.reduce((a: any, b: any) => a + priceOf(b), 0);

      accumulative += total;
      accumulativeSold += sold;
      accumulativeAvailable += available;
      accumulativeRevenue += revenue;

      rangeArray.push({
        minRange: bucketMin,
        maxRange: bucketMax,
        total: allUnits.length,
        units: total,
        accumulativeUnit: accumulative,
        sold: sold,
        accumulativeSold: accumulativeSold,
        available: available,
        accumulativeAvailable: accumulativeAvailable,
        revenue: numToCurrency.format(revenue),
        accumulativeRevenue: `${numToCurrency.format(accumulativeRevenue)} (${
          totalRevenue ? ((accumulativeRevenue / totalRevenue) * 100).toFixed(2) : '0.00'
        }%)`,
      });
    }

    return rangeArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, tierType, statusType, project._id, project.status, rangeSize, debouncedStartValueInput, debouncedEndValueInput]);

  const downloadRange = (type: string, data: any) => {
    let headers = [
      {
        label: 'Range',
        id: 'range',
      },
      {
        label: 'Unit Count',
        id: 'units',
      },
      {
        label: 'Accumulative Unit Count',
        id: 'accumulativeUnit',
      },
      {
        label: 'Sold',
        id: 'sold',
      },
      {
        label: 'Available',
        id: 'available',
      },
      {
        label: 'Accumulative Available',
        id: 'accumulativeAvailable',
      },
      {
        label: 'Revenue',
        id: 'revenue',
      },
      {
        label: 'Accumulative Revenue',
        id: 'accumulativeRevenue',
      },
    ];

    let widths = {
      range: 15,
      units: 15,
      accumulativeUnit: 15,
      sold: 15,
      available: 15,
      accumulativeAvailable: 15,
      revenue: 15,
      accumulativeRevenue: 15,
    };

    let pdfWidths = {
      range: 200,
      units: 200,
      accumulativeUnit: 200,
      sold: 200,
      available: 200,
      accumulativeAvailable: 200,
      revenue: 200,
      accumulativeRevenue: 200,
    };

    let sheetTitle = `${project.name} - Daily Summary`;

    let units = data.map((data: any) => {
      return {
        ...data,
        range: `${numToCurrency.format(data.minRange)} to ${numToCurrency.format(data.maxRange)}`,
        accumulativeAvailable: `${data.accumulativeAvailable} (${((data.accumulativeAvailable / data.total) * 100).toFixed(2)}%)`,
      };
    });

    if (type === 'excel') {
      downloadExcel([units], [headers], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([units], [headers], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  return (
    <div>
      <Typography variant={'h5'} gutterBottom>
        Range Summary
      </Typography>
      <Flex sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Range Difference"
          type="number"
          size="small"
          value={rangeSizeInput}
          onChange={(e) => setRangeSizeInput(e.target.value)}
          onBlur={commitNow}
          onKeyDown={commitOnEnter}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
        <TextField
          label="Starting Value"
          type="number"
          size="small"
          placeholder="Auto"
          value={startValueInput}
          onChange={(e) => setStartValueInput(e.target.value)}
          onBlur={commitNow}
          onKeyDown={commitOnEnter}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
        <TextField
          label="Ending Value"
          type="number"
          size="small"
          placeholder="Auto"
          value={endValueInput}
          onChange={(e) => setEndValueInput(e.target.value)}
          onBlur={commitNow}
          onKeyDown={commitOnEnter}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      </Flex>
      {rangeData.length >= MAX_BUCKETS ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Showing the first {MAX_BUCKETS} ranges. Increase the range difference to see the full spread.
        </Typography>
      ) : null}
      <StandardTable download={downloadRange} columns={rangeColumns} data={rangeData} />
    </div>
  );
};

interface ChildProps {
  units: IUnit[]
  tierType: string;
  statusType: string;
}

interface IRange {
  minRange: number;
  maxRange: number;
  total: number;
  sold: number;
  available: number;
  accumulativeUnit: number;
  revenue: string;
  accumulativeRevenue: string;
  units: number;
  accumulativeSold: number;
  accumulativeAvailable: number;
}

export default RangeSummary;
